import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { CourseAdminApi } from '../../services/api/CourseAdminApi';
import { OrgAdminApi } from '../../services/api/OrgAdminApi';

const TeacherContext = createContext(null);

// Backend v1 stubs sometimes return { status: 'stub' } or an empty array —
// UI treats stubs as "backend pending" rather than as real data.
const isStubResponse = (data) => {
  if (data == null) return true;
  if (Array.isArray(data)) return false;
  return data.status === 'stub' || data.stub === true;
};

async function readJson(resp) {
  if (!resp || !resp.ok) return null;
  return resp.json().catch(() => null);
}

export function TeacherProvider({ children }) {
  const { user, accessToken, getAdminOrgIds, hasAnyOrgAdminRole } = useAuth();

  const [classes, setClasses] = useState([]);
  const [atRiskStudents, setAtRiskStudents] = useState([]);
  const [atRiskStub, setAtRiskStub] = useState(false);
  const [rosterByCourse, setRosterByCourse] = useState({});
  const [progressSummaryByCourse, setProgressSummaryByCourse] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const teacher = useMemo(() => ({
    userId: user?.userId ?? null,
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    displayName: [user?.firstName, user?.lastName].filter(Boolean).join(' ') || (user?.email || 'Teacher'),
  }), [user]);

  const refresh = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const coursesResp = await CourseAdminApi.getMyAdminCourses();
      const coursesData = await readJson(coursesResp);
      const courseList = Array.isArray(coursesData) ? coursesData : [];
      setClasses(courseList);

      const orgIds = typeof getAdminOrgIds === 'function' ? getAdminOrgIds() : [];
      if (orgIds.length > 0) {
        const atRiskResps = await Promise.all(orgIds.map(id => OrgAdminApi.getAtRiskStudents(id)));
        const atRiskPayloads = await Promise.all(atRiskResps.map(readJson));
        const anyStub = atRiskPayloads.some(isStubResponse);
        const merged = atRiskPayloads.flatMap(p => (Array.isArray(p) ? p : []));
        setAtRiskStudents(merged);
        setAtRiskStub(anyStub);
      } else {
        setAtRiskStudents([]);
        setAtRiskStub(false);
      }

      const firstCourseId = courseList[0]?.endeavorId ?? courseList[0]?.id ?? null;
      if (firstCourseId != null) {
        const [rosterResp, summaryResp] = await Promise.all([
          CourseAdminApi.getRoster(firstCourseId),
          CourseAdminApi.getProgressSummary(firstCourseId),
        ]);
        const [rosterData, summaryData] = await Promise.all([readJson(rosterResp), readJson(summaryResp)]);
        setRosterByCourse({ [firstCourseId]: Array.isArray(rosterData) ? rosterData : [] });
        setProgressSummaryByCourse({ [firstCourseId]: summaryData || null });
      } else {
        setRosterByCourse({});
        setProgressSummaryByCourse({});
      }
    } catch (e) {
      setError(e?.message || 'Failed to load teacher data');
    } finally {
      setLoading(false);
    }
  }, [accessToken, getAdminOrgIds]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const firstCourseId = useMemo(
    () => classes[0]?.endeavorId ?? classes[0]?.id ?? null,
    [classes]
  );

  const value = useMemo(() => ({
    teacher,
    loading,
    error,
    refresh,

    classes,
    hasAnyOrgAdmin: typeof hasAnyOrgAdminRole === 'function' ? hasAnyOrgAdminRole() : false,

    atRiskStudents,
    atRiskStub,

    firstCourseId,
    roster: firstCourseId != null ? (rosterByCourse[firstCourseId] || []) : [],
    progressSummary: firstCourseId != null ? (progressSummaryByCourse[firstCourseId] || null) : null,
  }), [
    teacher, loading, error, refresh,
    classes, hasAnyOrgAdminRole, atRiskStudents, atRiskStub,
    firstCourseId, rosterByCourse, progressSummaryByCourse,
  ]);

  return (
    <TeacherContext.Provider value={value}>
      {children}
    </TeacherContext.Provider>
  );
}

export function useTeacher() {
  const context = useContext(TeacherContext);
  if (!context) {
    throw new Error('useTeacher must be used within a TeacherProvider');
  }
  return context;
}

export default TeacherContext;
